// web/src/components/ceremony/envelope.ts — resolve the requester's AES-GCM
// envelope (the encrypted-secret blob) needed to OPEN a record at quorum.
//
// To recombine and reveal, the requester needs the full `Envelope`:
//   { v, alg, iv, ciphertext }
// The K decrypted shares rebuild the data key; `combineAndOpen(shares, envelope)`
// then AES-GCM-decrypts `envelope.ciphertext` with it. The IV and version live in
// the record's PUBLIC `envelopeMeta`; the CIPHERTEXT is the encrypted blob.
//
// ── KNOWN BACKEND GAP (reported to the orchestrator) ───────────────────────────
// The seal write-path (enroll/seal.ts → POST /records) sends only the PUBLIC
// envelope params { v, aeadAlg, sssScheme, iv } in `record.envelopeMeta`, and the
// API allow-lists exactly those four keys (api/src/routes/records.ts ENVELOPE_ALLOWED)
// — it REJECTS a `ciphertext` field (422 envelope_extra) and drops the blob at seal.
// No read endpoint (GET /records/:id, GET /records/:id/my-shard, GET /quorum/:id/*)
// returns the envelope ciphertext to the requester. So today this resolver returns
// null and the ceremony surfaces the gap honestly rather than faking a reveal (R11).
//
// This module is written so that the MOMENT the backend delivers the ciphertext
// (either inside `envelopeMeta` once allow-listed, or via a dedicated field), the
// requester reveal lights up with NO further web change.
import type { Envelope } from '../../crypto';
import type { RecordDetail } from '../../../../shared/types';

/** A loosely-typed view of whatever the server stuffs into envelopeMeta / the record. */
type LooseMeta = Record<string, unknown>;

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Parse a record's envelope params + ciphertext into a usable `Envelope`, or null
 * when the ciphertext is not available to this device (the documented gap above).
 *
 * Accepts the ciphertext from any of the forward-compatible shapes the backend
 * might adopt: a `ciphertext` key inside `envelopeMeta`, or a sibling
 * `envelopeCiphertext` on the record detail. The IV/version come from envelopeMeta.
 */
export function parseEnvelope(detail: RecordDetail): Envelope | null {
  const rawMeta = detail.record.envelopeMeta;
  let meta: LooseMeta;
  try {
    meta = (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) as LooseMeta;
  } catch {
    return null;
  }
  if (!meta || typeof meta !== 'object') return null;

  const iv = str(meta.iv);
  // Forward-compatible: ciphertext may ride inside envelopeMeta (once the API
  // allow-lists it) or as a sibling field on the record detail projection.
  const ciphertext =
    str(meta.ciphertext) ??
    str((detail.record as unknown as LooseMeta).envelopeCiphertext) ??
    str((detail as unknown as LooseMeta).envelopeCiphertext);

  // The blob is the load-bearing part — without it there is nothing to decrypt.
  if (!iv || !ciphertext) return null;

  const v = typeof meta.v === 'number' ? meta.v : 1;
  return {
    v: v as Envelope['v'],
    alg: 'AES-256-GCM',
    iv: iv as Envelope['iv'],
    ciphertext: ciphertext as Envelope['ciphertext'],
  };
}
